import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Download } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { styles } from '../theme';
import { API_URL, fetchWithAuth } from '../api';
import { exportReportToPDF } from '../utils/reportPdf';
import { formatCurrency } from '../utils/formatUtils';
import { getPHTDate } from '../utils/dateUtils';

const fmt = (v) => formatCurrency(v);
const defaultDates = () => {
  const to = getPHTDate();
  const from = getPHTDate();
  from.setDate(from.getDate() - 7);
  const s = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { from: s(from), to: s(to) };
};

const PosSalesByItemReport = ({ token }) => {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [branchId, setBranchId] = useState('');
  const [branches, setBranches] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const { from, to } = defaultDates();
    setDateFrom(from);
    setDateTo(to);
    fetchWithAuth(`${API_URL}/branchesdropdown`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.json()).then(setBranches).catch(err => console.error(err));
  }, [token]);

  const generate = useCallback(async () => {
    if (!dateFrom || !dateTo) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo, branch_id: branchId });
      const res = await fetchWithAuth(`${API_URL}/pos/reports/sales-by-item?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      setRows(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token, dateFrom, dateTo, branchId]);

  useEffect(() => { if (dateFrom && dateTo) generate(); }, [dateFrom, dateTo, generate]);

  const totalNet = rows.reduce((sum, r) => sum + Number(r.net_sales || 0), 0);

  const topItems = useMemo(() => {
    return [...rows]
      .sort((a, b) => Number(b.net_sales || 0) - Number(a.net_sales || 0))
      .slice(0, 10)
      .map(r => ({ item_name: r.item_name, net_sales: Number(r.net_sales || 0) }));
  }, [rows]);

  const exportPdf = () => {
    exportReportToPDF({
      title: 'POS Sales by Item Report',
      subtitle: `${dateFrom} to ${dateTo}`,
      columns: [
        { header: 'Item', key: 'item_name' }, { header: 'SKU', key: 'sku' }, { header: 'Category', key: 'category_name' },
        { header: 'Qty Sold', key: 'quantity_sold', align: 'right' },
        { header: 'Gross', key: 'gross_sales', align: 'right' },
        { header: 'Discount', key: 'discount_total', align: 'right' },
        { header: 'Net Sales', key: 'net_sales', align: 'right' }
      ],
      rows: rows.map(r => ({ ...r, gross_sales: fmt(r.gross_sales), discount_total: fmt(r.discount_total), net_sales: fmt(r.net_sales) })),
      filename: `pos_sales_by_item_${dateFrom}_to_${dateTo}.pdf`
    });
  };

  return (
    <div>
      <h1 style={styles.title}>Sales by Item Report</h1>
      <div style={{ ...styles.card, marginTop: '16px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', marginBottom: '16px' }}>
          <div style={styles.formGroup}><label style={styles.label}>From</label><input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={styles.input} /></div>
          <div style={styles.formGroup}><label style={styles.label}>To</label><input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={styles.input} /></div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Branch</label>
            <select value={branchId} onChange={(e) => setBranchId(e.target.value)} style={styles.input}>
              <option value="">All Branches</option>
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={generate} disabled={loading} style={{ ...styles.button, ...styles.buttonReport }}>
            <Download size={18} /> {loading ? 'Generating...' : 'Generate Report'}
          </button>
          {rows.length > 0 && <button onClick={exportPdf} style={{ ...styles.button, ...styles.buttonSecondary }}>Export PDF</button>}
        </div>
      </div>

      {topItems.length > 0 && (
        <div style={{ ...styles.card, marginTop: '16px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>Top 10 Items by Net Sales</h3>
          <div style={{ height: `${Math.max(topItems.length * 36, 200)}px`, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topItems} layout="vertical" margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e5e7eb" />
                <XAxis type="number" tickFormatter={(v) => `₱${v.toLocaleString()}`} tick={{ fill: '#4b5563', fontSize: 12 }} />
                <YAxis type="category" dataKey="item_name" width={150} tick={{ fill: '#4b5563', fontSize: 12 }} />
                <Tooltip formatter={(v) => `₱${fmt(v)}`} />
                <Bar dataKey="net_sales" name="Net Sales" fill="#4f46e5" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div style={{ ...styles.card, marginTop: '16px', padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Item</th><th style={styles.th}>SKU</th><th style={styles.th}>Category</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Qty Sold</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Gross</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Discount</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Net Sales</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Txns</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td style={styles.td}>{r.item_name}</td>
                  <td style={styles.td}>{r.sku || '-'}</td>
                  <td style={styles.td}>{r.category_name || 'Uncategorized'}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>{r.quantity_sold}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>₱{fmt(r.gross_sales)}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>₱{fmt(r.discount_total)}</td>
                  <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600 }}>₱{fmt(r.net_sales)}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>{r.transaction_count}</td>
                </tr>
              ))}
              {rows.length === 0 && !loading && (
                <tr><td colSpan={8} style={{ ...styles.td, textAlign: 'center', color: '#9ca3af' }}>No sales data for this period.</td></tr>
              )}
              {rows.length > 0 && (
                <tr style={{ backgroundColor: '#ecfeff' }}>
                  <td style={{ ...styles.td, fontWeight: 700 }} colSpan={6}>TOTAL NET SALES</td>
                  <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700 }}>₱{fmt(totalNet)}</td>
                  <td style={styles.td}></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PosSalesByItemReport;
